import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Save, Users, FileText, Download, Upload, Database, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SettingsTab = ({ stats, onStatsUpdate }) => {
  const [systemConfig, setSystemConfig] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const importFileRef = useRef(null);

  const availableFields = [
    { id: 'vorname', label: 'Vorname', type: 'text' },
    { id: 'nachname', label: 'Nachname', type: 'text' },
    { id: 'geburtsdatum', label: 'Geburtsdatum', type: 'date' },
    { id: 'email', label: 'E-Mail', type: 'email' }
  ];

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const [configRes, authRes] = await Promise.all([
        axios.get(`${API}/admin/system/config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/auth-config`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSystemConfig(configRes.data);
      setAuthConfig(authRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemUpdate = async (key, value) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/system/config`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Änderung gespeichert');
      if (onStatsUpdate) onStatsUpdate();
      fetchConfigs();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleAuthFieldToggle = (fieldId) => {
    const currentFields = authConfig.login_fields || [];
    let newFields;

    if (currentFields.includes(fieldId)) {
      // Remove field
      newFields = currentFields.filter(f => f !== fieldId);
    } else {
      // Add field
      newFields = [...currentFields, fieldId];
    }

    setAuthConfig({ ...authConfig, login_fields: newFields });
  };

  const handleAuthSave = async () => {
    if (authConfig.login_fields.length < 2) {
      toast.error('Mindestens 2 Login-Felder erforderlich');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/auth-config`,
        authConfig.login_fields,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Login-Felder gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Backup Export Function
  const handleBackupExport = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/backup/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `msy_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      const kurseCount = response.data.data?.kurse?.length || 0;
      const teilnehmerCount = response.data.data?.teilnehmer?.length || 0;
      const textbausteineCount = response.data.data?.textbausteine?.length || 0;
      
      toast.success(`Backup erstellt: ${kurseCount} Kurse, ${teilnehmerCount} Teilnehmer, ${textbausteineCount} Textbausteine`);
    } catch (error) {
      toast.error('Fehler beim Erstellen des Backups');
    } finally {
      setBackupLoading(false);
    }
  };

  // Backup Import Function
  const handleBackupImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setBackupLoading(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/backup/import`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const results = response.data;
      const messages = [];
      
      if (results.kurse.imported > 0) messages.push(`${results.kurse.imported} Kurse`);
      if (results.teilnehmer.imported > 0) messages.push(`${results.teilnehmer.imported} Teilnehmer`);
      if (results.textbausteine.imported > 0) messages.push(`${results.textbausteine.imported} Textbausteine`);
      
      if (messages.length > 0) {
        toast.success(`Importiert: ${messages.join(', ')}`);
      } else {
        toast.info('Keine neuen Daten importiert (bereits vorhanden)');
      }
      
      if (results.config_restored) {
        toast.success('Einstellungen wiederhergestellt');
      }
      
      // Refresh data
      fetchConfigs();
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Importieren - ungültiges Backup-Format');
    } finally {
      setBackupLoading(false);
      if (importFileRef.current) {
        importFileRef.current.value = '';
      }
    }
  };

  if (loading) {
    return <div>Lade...</div>;
  }

  return (
    <div className="space-y-6">
      {/* System Settings - Modern */}
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle>Systemeinstellungen</CardTitle>
          <CardDescription>
            Passe die Grundeinstellungen für dein System an
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Online/Offline mit Farbakzent */}
          <div className="flex items-center justify-between p-4 rounded-lg border-2 transition-all" style={{
            borderColor: systemConfig?.online ? '#10b981' : '#ef4444',
            backgroundColor: systemConfig?.online ? '#f0fdf4' : '#fef2f2'
          }}>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Label htmlFor="online-switch" className="text-base font-semibold cursor-pointer" style={{
                  color: systemConfig?.online ? '#059669' : '#dc2626'
                }}>
                  System {systemConfig?.online ? 'Online' : 'Offline'}
                </Label>
              </div>
              <p className="text-sm text-gray-600">
                {systemConfig?.online 
                  ? 'Teilnehmer können sich anmelden'
                  : 'Teilnehmer können sich nicht anmelden'}
              </p>
            </div>
            <Switch
              id="online-switch"
              checked={systemConfig?.online || false}
              onCheckedChange={(checked) => handleSystemUpdate('online', checked)}
              data-testid="online-switch"
              className="data-[state=checked]:bg-green-500"
            />
          </div>

          <div className="border-t border-gray-200"></div>

          {/* Betriebsmodus mit Farbakzent */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-medium">Betriebsmodus</Label>
              <p className="text-sm text-gray-500 mt-1">
                Wähle, welche Informationen angezeigt werden
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Anmeldung Modus */}
              <button
                onClick={() => handleSystemUpdate('modus', 'Anmeldung')}
                data-testid="settings-mode-anmeldung"
                className={`p-5 rounded-lg border-2 transition-all text-left ${
                  systemConfig?.modus === 'Anmeldung'
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    systemConfig?.modus === 'Anmeldung' ? 'bg-blue-600' : 'bg-gray-100'
                  }`}>
                    <Users className={`h-5 w-5 ${systemConfig?.modus === 'Anmeldung' ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold mb-1 ${
                      systemConfig?.modus === 'Anmeldung' ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      Anmeldung
                    </h3>
                    <p className="text-sm text-gray-600">
                      Kursdetails, Lehrer und Zeiten
                    </p>
                    {systemConfig?.modus === 'Anmeldung' && (
                      <Badge className="mt-2 bg-blue-600 text-white">Aktiv</Badge>
                    )}
                  </div>
                </div>
              </button>

              {/* Prüfung Modus */}
              <button
                onClick={() => handleSystemUpdate('modus', 'Prüfung')}
                data-testid="settings-mode-pruefung"
                className={`p-5 rounded-lg border-2 transition-all text-left ${
                  systemConfig?.modus === 'Prüfung'
                    ? 'border-purple-500 bg-purple-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    systemConfig?.modus === 'Prüfung' ? 'bg-purple-600' : 'bg-gray-100'
                  }`}>
                    <FileText className={`h-5 w-5 ${systemConfig?.modus === 'Prüfung' ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold mb-1 ${
                      systemConfig?.modus === 'Prüfung' ? 'text-purple-900' : 'text-gray-900'
                    }`}>
                      Prüfung
                    </h3>
                    <p className="text-sm text-gray-600">
                      Prüfungsergebnisse und Noten
                    </p>
                    {systemConfig?.modus === 'Prüfung' && (
                      <Badge className="mt-2 bg-purple-600 text-white">Aktiv</Badge>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Authentication Configuration - Modern */}
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle>Login-Felder</CardTitle>
          <CardDescription>
            Wähle mindestens 2 Felder für die Teilnehmer-Anmeldung
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableFields.map(field => {
              const isSelected = authConfig?.login_fields?.includes(field.id) || false;
              return (
                <button
                  key={field.id}
                  onClick={() => handleAuthFieldToggle(field.id)}
                  data-testid={`auth-field-${field.id}`}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className={`font-semibold mb-1 ${
                        isSelected ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {field.label}
                      </h4>
                      <p className="text-sm text-gray-500">{field.type}</p>
                    </div>
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      {isSelected && (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{authConfig?.login_fields?.length || 0}</span> von {availableFields.length} Feldern ausgewählt
              </p>
              {authConfig?.login_fields?.length < 2 && (
                <p className="text-sm text-amber-600 mt-1">
                  ⚠️ Mindestens 2 Felder erforderlich
                </p>
              )}
            </div>
            <Button
              onClick={handleAuthSave}
              disabled={authConfig?.login_fields?.length < 2}
              data-testid="save-auth-config-button"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300"
            >
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backup & Restore */}
      <Card className="border border-gray-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-gray-600" />
            <div>
              <CardTitle>Backup & Wiederherstellung</CardTitle>
              <CardDescription>
                Sichere alle Daten (Kurse, Teilnehmer, Textbausteine, Einstellungen)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Export */}
            <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-green-600">
                  <Download className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-1">Backup erstellen</h3>
                  <p className="text-sm text-green-700 mb-3">
                    Exportiert alle Daten als JSON-Datei
                  </p>
                  <Button
                    onClick={handleBackupExport}
                    disabled={backupLoading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="backup-export-btn"
                  >
                    {backupLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Backup herunterladen
                  </Button>
                </div>
              </div>
            </div>

            {/* Import */}
            <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-blue-600">
                  <Upload className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 mb-1">Backup wiederherstellen</h3>
                  <p className="text-sm text-blue-700 mb-3">
                    Importiert Daten aus einer Backup-Datei
                  </p>
                  <input
                    type="file"
                    ref={importFileRef}
                    onChange={handleBackupImport}
                    accept=".json"
                    className="hidden"
                    data-testid="backup-import-input"
                  />
                  <Button
                    onClick={() => importFileRef.current?.click()}
                    disabled={backupLoading}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-100"
                    data-testid="backup-import-btn"
                  >
                    {backupLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Backup importieren
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Hinweis:</strong> Beim Import werden nur neue Daten hinzugefügt. Bestehende Daten werden nicht überschrieben.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsTab;